import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AuthProvider } from '@/auth/AuthProvider';
import { AgencyProvider } from '@/auth/AgencyProvider';
import { PermissionsProvider } from '@/auth/PermissionsProvider';
import { RequireAuth } from '@/auth/RequireAuth';
import { LegalProvider } from '@/legal/LegalProvider';
import { TeamProvider } from '@/data/useTeam';
import { ProfileProvider } from '@/data/useProfile';
import { AppLayout } from '@/components/layout/AppLayout';
import { EmptyState } from '@/components/EmptyState';
import { NAV_ROUTES } from '@/navigation';
import { SignIn } from '@/routes/SignIn';
import { SignUp } from '@/routes/SignUp';
import { NotFound } from '@/routes/NotFound';
import { RouteFallback } from '@/components/common/RouteFallback';
import { RealtimeProvider } from '@/realtime/RealtimeProvider';
import { useRejectionNotices } from '@/realtime/useRejectionNotices';
import { ChatUnreadProvider } from '@/realtime/ChatUnread';
import type { RealtimeTransport } from '@/lib/realtime';

const InviteAccept = React.lazy(() => import('@/routes/InviteAccept').then((m) => ({ default: m.InviteAccept })));

/* Route-level code splitting: every screen is its own chunk, loaded on first visit. The Suspense
 * boundary lives in AppLayout, around the Outlet, so the rail and the top bar stay while one loads.
 * SignIn and NotFound are tiny and stay in the entry chunk. */
const Dashboard = React.lazy(() => import('@/routes/Dashboard').then((m) => ({ default: m.Dashboard })));
const CarrierInvoices = React.lazy(() =>
  import('@/routes/CarrierInvoices').then((m) => ({ default: m.CarrierInvoices })),
);
const Carriers = React.lazy(() => import('@/routes/Carriers'));
const Trucks = React.lazy(() => import('@/routes/Trucks'));
const Drivers = React.lazy(() => import('@/routes/Drivers'));
const Brokers = React.lazy(() => import('@/routes/Brokers'));
const BrokerSetups = React.lazy(() => import('@/routes/BrokerSetups'));
const BrokerPayments = React.lazy(() => import('@/routes/BrokerPayments'));
const Settings = React.lazy(() => import('@/routes/Settings'));
const SettingsTeam = React.lazy(() => import('@/routes/SettingsTeam'));
const SettingsPermissions = React.lazy(() => import('@/routes/SettingsPermissions'));
const SettingsProfile = React.lazy(() => import('@/routes/SettingsProfile'));
const Loads = React.lazy(() => import('@/routes/Loads'));
const LoadNew = React.lazy(() => import('@/routes/LoadNew'));
const LoadPage = React.lazy(() => import('@/routes/LoadPage'));
const Dispatch = React.lazy(() => import('@/routes/Dispatch'));
const BolsPods = React.lazy(() => import('@/routes/BolsPods'));
const Facilities = React.lazy(() => import('@/routes/Facilities'));
const Chat = React.lazy(() => import('@/routes/Chat'));
// owner-fees: the weekly owner statements, one statement, its owner copy, and Settings > Fees.
const OwnerStatements = React.lazy(() => import('@/routes/OwnerStatements'));
const OwnerStatementPage = React.lazy(() => import('@/routes/OwnerStatementPage'));
const OwnerStatementPrint = React.lazy(() => import('@/routes/OwnerStatementPrint'));
const DriverSettlements = React.lazy(() => import('@/routes/DriverSettlements'));
const DriverSettlementPage = React.lazy(() => import('@/routes/DriverSettlementPage'));
const DriverSettlementPrint = React.lazy(() => import('@/routes/DriverSettlementPrint'));
const SettingsFees = React.lazy(() => import('@/routes/SettingsFees'));
// fleet-driver-pay: Settings > Driver pay (who pays the drivers, the default pay, the settlement rules).
const SettingsPay = React.lazy(() => import('@/routes/SettingsPay'));
// invoice-pdf: Settings > Company (the identity on every invoice, the logo, invoice delivery).
const SettingsCompany = React.lazy(() => import('@/routes/SettingsCompany'));
const SettingsFactoring = React.lazy(() => import('@/routes/SettingsFactoring'));
// agency-billing: Settings > Billing (the Coreway plan, its price and invoices, subscribe and pay).
const SettingsBilling = React.lazy(() => import('@/routes/SettingsBilling'));
// doclists (round 3): the two document lists across loads, Support, and the dispatchers' roster.
const RateConfirmations = React.lazy(() => import('@/routes/RateConfirmations'));
const Receipts = React.lazy(() => import('@/routes/Receipts'));
const Support = React.lazy(() => import('@/routes/Support'));
const AssignedDrivers = React.lazy(() => import('@/routes/AssignedDrivers'));
const DispatcherPay = React.lazy(() => import('@/routes/DispatcherPay'));
/** `/dispatchers` is a menu group with no screen of its own: it opens its Dispatch board. */
const DispatchersIndex = () => <Navigate to="/dispatchers/dispatch" replace />;
const Tasks = React.lazy(() => import('@/routes/Tasks'));
const Activity = React.lazy(() => import('@/routes/Activity'));
// broker credit (round 3): the credit check before booking.
const CreditCheck = React.lazy(() => import('@/routes/CreditCheck'));
const BrokerReport = React.lazy(() => import('@/routes/BrokerReport'));
// reports: /reports (revenue by carrier / driver / truck / dispatcher / broker, miles, invoices, CSV).
const Reports = React.lazy(() => import('@/routes/Reports'));
// documents: carrier, truck and driver documents, the carrier packages and a package's cover sheet.
const Documentation = React.lazy(() => import('@/routes/Documentation'));
const CarrierPackages = React.lazy(() => import('@/routes/CarrierPackages'));
const CarrierPackagePrint = React.lazy(() => import('@/routes/CarrierPackagePrint'));
// fleet-driver-pay T5.3: the announcements inbox, reached from the top bar's megaphone (not in the menu).
const Announcements = React.lazy(() => import('@/routes/Announcements'));

/**
 * One route per menu entry, generated from the same list the sidebar renders, so the two can never
 * drift. Every one of them is an honest empty state until its screen is built.
 *
 * `BUILT` is the exception list: a route in it renders its real screen instead.
 */
const BUILT: Record<string, React.ComponentType> = {
  dashboard: Dashboard,
  carriers: Carriers,
  carriersInvoices: CarrierInvoices,
  carriersTrucks: Trucks,
  carriersDrivers: Drivers,
  brokers: Brokers,
  brokersSetups: BrokerSetups,
  brokersPayments: BrokerPayments,
  loads: Loads,
  dispatchersDispatch: Dispatch,
  brokersBolsPods: BolsPods,
  brokersFacilities: Facilities,
  chat: Chat,
  carriersOwnerStatements: OwnerStatements,
  carriersDriverSettlements: DriverSettlements,
  brokersRateConfirmations: RateConfirmations,
  brokersReceipts: Receipts,
  support: Support,
  dispatchers: DispatchersIndex,
  dispatchersAssignedDrivers: AssignedDrivers,
  dispatchersPay: DispatcherPay,
  tasks: Tasks,
  activity: Activity,
  brokersCreditCheck: CreditCheck,
  reports: Reports,
  documentation: Documentation,
  carriersPackages: CarrierPackages,
};

/** The rejection toast listens on `user:<self>` once for the whole shell (design 4.2). */
function RejectionNotices() {
  useRejectionNotices();
  return null;
}

/**
 * `realtime` is the transport the shell's RealtimeProvider uses. Production passes nothing and the
 * provider builds the Centrifugo one; tests pass the fake, so App never opens a WebSocket in jsdom.
 */
export function App({ client, realtime }: { client: SupabaseClient; realtime?: RealtimeTransport }) {
  return (
    <AuthProvider client={client}>
      <BrowserRouter>
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          {/* self-signup: public, the door of a new agency's trial (the link and OAuth land on /signin). */}
          <Route path="/signup" element={<SignUp />} />
          {/* Public like /signin (F3): the invitee is not a member yet, so no guard and no agency
              providers; the page reads only her own invites. Its own chunk, its own Suspense. */}
          <Route
            path="/invite/:inviteId"
            element={
              <React.Suspense fallback={<RouteFallback />}>
                <InviteAccept />
              </React.Suspense>
            }
          />
          <Route element={<RequireAuth />}>
            {/* The agency, role, team names and own profile are read once, under the auth guard (SignIn
                never queries them), around the layout so the rail, the top bar and every screen share them. */}
            {/* The Terms gate (terms-acceptance) comes first: until the user accepted the versions in force,
                none of the agency providers below mount, so no agency, team or realtime read happens. */}
            <Route
              element={
                <LegalProvider>
                  <AgencyProvider>
                    <PermissionsProvider>
                      <TeamProvider>
                        <ProfileProvider>
                          {/* One realtime connection per signed-in session, inside the agency providers
                              (a session and an agency exist); every screen's live hooks share it. */}
                          <RealtimeProvider transport={realtime}>
                            <RejectionNotices />
                            {/* The chat's always-mounted consumer (driver-office-chat): the badge, the
                                toast, and the hints the chat screens re-read on. */}
                            <ChatUnreadProvider>
                              <AppLayout />
                            </ChatUnreadProvider>
                          </RealtimeProvider>
                        </ProfileProvider>
                      </TeamProvider>
                    </PermissionsProvider>
                  </AgencyProvider>
                </LegalProvider>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              {NAV_ROUTES.map((route) => {
                const Screen = BUILT[route.id];
                return (
                  <Route
                    key={route.id}
                    path={route.path}
                    element={
                      Screen ? <Screen /> : <EmptyState id={route.id} icon={route.icon} />
                    }
                  />
                );
              })}
              {/* Outside NAVIGATION: reached from the New load buttons and from a load's number; the
                  rail keeps Loads highlighted (it matches the /loads prefix). */}
              <Route path="/loads/new" element={<LoadNew />} />
              <Route path="/loads/:loadId" element={<LoadPage />} />
              {/* One thread of the chat; the rail keeps Chat highlighted (coversSubpaths). */}
              <Route path="/chat/:threadId" element={<Chat />} />
              {/* owner-fees: reached from the Owner Statements list (the rail keeps it highlighted). */}
              <Route path="/carriers/owner-statements/:statementId" element={<OwnerStatementPage />} />
              <Route path="/carriers/owner-statements/:statementId/print" element={<OwnerStatementPrint />} />
              {/* fleet-driver-pay: reached from the Driver Settlements list (the rail keeps it highlighted). */}
              <Route path="/carriers/driver-settlements/:settlementId" element={<DriverSettlementPage />} />
              <Route path="/carriers/driver-settlements/:settlementId/print" element={<DriverSettlementPrint />} />
              {/* documents: a carrier package's printable cover sheet (the rail keeps Packages highlighted). */}
              <Route path="/carriers/packages/:carrierId/print" element={<CarrierPackagePrint />} />
              {/* broker-report: reached from the broker sheet and the broker credit sheet (the rail keeps Brokers open). */}
              <Route path="/brokers/:brokerId/report" element={<BrokerReport />} />
              {/* fleet-driver-pay T5.3: everything the Coreway team sent, read or not (the top bar's megaphone). */}
              <Route path="/announcements" element={<Announcements />} />
              {/* Outside NAVIGATION: reached from the gear in the user card. */}
              <Route path="/settings" element={<Settings />}>
                <Route index element={<Navigate to="team" replace />} />
                <Route path="team" element={<SettingsTeam />} />
                <Route path="permissions" element={<SettingsPermissions />} />
                <Route path="profile" element={<SettingsProfile />} />
                <Route path="fees" element={<SettingsFees />} />
                <Route path="pay" element={<SettingsPay />} />
                <Route path="company" element={<SettingsCompany />} />
                <Route path="factoring" element={<SettingsFactoring />} />
                <Route path="billing" element={<SettingsBilling />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
